<ng-container *ngIf="account$ | async as account; else noAccount">
  <mat-card class="card-table">
    <mat-progress-bar [mode]="(loading$ | async) ? 'indeterminate' : 'determinate'"></mat-progress-bar>
    <mat-card-title>
      <span>
        {{ 'patient-account-clearance.title' | translate }}
      </span>
      <div fxFlex></div>
      <div fxLayout="row" fxLayoutAlign="space-between center" fxLayoutGap="10px">
        <mat-icon
          *ngIf="isAppointmentCanceled$ | async"
          class="mr-1"
          [matTooltip]="'patient-account-clearance.appointment-canceled-warning' | translate"
        >
          warning
        </mat-icon>
        <mat-slide-toggle
          *ngIf="courseOfCareToggleEnabled$ | async"
          [checked]="showAllCourseOfCareCpts$ | async"
          (change)="onCourseOfCareToggle($event)"
          color="primary"
        >
          {{ 'patient-account-clearance.course-of-care-toggle' | translate }}
        </mat-slide-toggle>
        <button
          mat-stroked-button
          class="ml-2 mr-2"
          [disabled]="isReprocessDisabled$ | async"
          mmxConfirmClick
          [message]="'patient-account-clearance.reprocess-confirmation' | translate"
          (confirm)="reprocessAccount(account)"
        >
          <mat-icon>replay</mat-icon>
          <span>
            {{ 'patient-account-clearance.reprocess' | translate }}
          </span>
        </button>
      </div>
    </mat-card-title>

    <mat-card-content>
      <div class="p-4">
        <mat-chip-listbox selectable (change)="onInsuranceSelectionChange($event)">
          <div *ngFor="let insurance of account.insurances" fxFlex fxLayoutAlign="start center">
            <mat-chip-option [value]="insurance">
              ({{ ('insurances.type.' + insurance.type) | translate }})
              {{ insurance.clinicCarrierName }}
              <ng-container *ngIf="insurance.getClearanceStatus(account.id) as clearanceStatus">
                Auth Status:
                <span [ngClass]="['color', clearanceStatus | accountClearanceStatusToColor]">
                  {{ ('statuses.account-clearance.' + clearanceStatus) | translate }}
                </span>
              </ng-container>
            </mat-chip-option>
            <ng-container *ngIf="insurance.getClearanceStatus(account.id) === clearanceStatuses.REQUIRED">
              <mat-icon
                class="pointer"
                (click)="addAuthorization(insurance)"
                [matTooltip]="'patient-account-clearance.add-authorization' | translate"
                >
                  add
                </mat-icon>
            </ng-container>
          </div>
        </mat-chip-listbox>
      </div>

      <ng-container *ngIf="tableData$ | async as tableData">
        <table
          mat-table
          [dataSource]="tableData.clearances"
          matSort
          [matSortActive]="tableData.sort.active"
          [matSortDirection]="tableData.sort.direction"
          (matSortChange)="sortData($event)"
        >
          <ng-container matColumnDef="code">
            <th mat-header-cell *matHeaderCellDef mat-sort-header> Code </th>
            <td mat-cell *matCellDef="let clearance">
              {{ clearance.code }}
              <ng-container *ngIf="shouldShowClearanceSourceBadge(
                clearance,
                tableData.appointment.id,
                tableData.appointmentCptCodes,
                showAuthNotScheduledCpts$ | async,
                showAllCourseOfCareCpts$ | async
              )">
                <mat-chip
                  class="auth-only-chip"
                  [matTooltip]="getClearanceSourceTooltipKey(clearance, tableData.appointment.id, tableData.appointmentCptCodes) | translate"
                >
                  {{ getClearanceSourceLabelKey(clearance, tableData.appointment.id, tableData.appointmentCptCodes) | translate }}
                </mat-chip>
              </ng-container>
            </td>
          </ng-container>

          <ng-container matColumnDef="appointmentDate">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>
              {{ 'patient-account-clearance.table.appointment-date' | translate }}
            </th>
            <td mat-cell *matCellDef="let clearance">
              <ng-container *ngIf="clearance.appointmentIsOrder; else appointmentStart">
                {{ 'patient-account-clearance.table.order' | translate }}
              </ng-container>
              <ng-template #appointmentStart>
                {{ clearance.appointmentStartAt | momentDateTime: clearance.appointmentTimezone }}
              </ng-template>
            </td>
          </ng-container>

          <ng-container matColumnDef="coverage">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>
              {{ 'patient-account-clearance.table.insurance-coverage' | translate }}
            </th>
            <td mat-cell *matCellDef="let clearance">
              <ng-container *ngIf="clearance.patientInsurance">
                {{ clearance.patientInsurance.clinicCarrierName }} ({{ clearance.patientInsurance.type }})
              </ng-container>
            </td>
          </ng-container>

          <ng-container matColumnDef="authorizationRequired">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>
              {{ 'patient-account-clearance.table.authorization-required' | translate }}
            </th>
            <td mat-cell *matCellDef="let clearance"> {{ clearance.isAuthRequired ? 'Yes' : 'No' }} </td>
          </ng-container>

          <ng-container matColumnDef="authorizationNumber">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>
              {{ 'patient-account-clearance.table.authorization-number' | translate }}
            </th>
            <td mat-cell *matCellDef="let clearance"> {{ clearance.authorization?.number }} </td>
          </ng-container>
          <ng-container matColumnDef="clearanceStatus">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>
              {{ 'patient-account-clearance.table.clearance-status' | translate }}
            </th>
            <td mat-cell *matCellDef="let clearance">
              <ng-container *ngIf="clearance.status">
                {{ ('statuses.account-procedure-clearance.' + clearance.status) | translate }}
              </ng-container>
            </td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="tableData.displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: tableData.displayedColumns;"></tr>
        </table>
      </ng-container>
    </mat-card-content>
  </mat-card>
</ng-container>

<ng-template #noAccount>
  <mat-card class="default">
    <mat-card-title>
      <span>
        {{ 'patient-account-clearance.title' | translate }}
      </span>
    </mat-card-title>
    <mat-card-content>
      <p class="text-warn">{{ 'patient-account-clearance.no-account' | translate }}</p>
    </mat-card-content>
  </mat-card>
</ng-template>
